import React from 'react';
import { useTranslation } from 'react-i18next';
/* Toaster is untyped JS (allowJs). */
import { toasterMsg } from '../../../../../../../Components/Toaster/Toaster';
import { PushIcon } from '../../../../../../../bombiix-icon';
import { selectedSourceRows, targetProductIds, useMeasurementsDispatch, useMeasurementsState } from '../MeasurementsProvider';

/** Glyph size (px) inside the group-header action button. */
const PUSH_ICON_SIZE = 20;

/**
 * Push action mounted at the right-hand end of the Spec Table column-group header (BM-9210). Push runs
 * in two clicks: the first arms it once the source product's POM rows are ticked, the second copies
 * those rows' size and grading values into every selected target product, matched on POM code. The
 * button reads module context directly, the way `SelectAllHeader` does, because AG-Grid renders header
 * components without the grid's own props.
 */
const PushHeaderAction: React.FC = () => {
  const { t } = useTranslation('bulk-edit');
  const state = useMeasurementsState();
  const dispatch = useMeasurementsDispatch();

  const source = state.products.find((product) => product.devOrderId === state.sourceDevOrderId);
  const sourceRowCount = source ? selectedSourceRows(state, source).length : 0;
  const targetCount = targetProductIds(state).size;
  /* Clickable as soon as the source has ticked rows, and it STAYS clickable while armed with no targets —
     otherwise arming with only the source selected would disable the button and trap the user with no way
     to switch Push back off (BM-9210). */
  const enabled = sourceRowCount > 0;
  /* The second click only fires the push once there is somewhere to push to; otherwise it disarms. */
  const canApply = state.pushArmed && targetCount > 0;

  /* No visible tooltip on this button, so the state-specific wording carries on the accessible name. */
  const actionLabel = (): string => {
    if (!state.pushArmed) return sourceRowCount > 0 ? t('MEASUREMENTS_MODULE.PUSH') : t('MEASUREMENTS_MODULE.PUSH_SELECT_SOURCE');
    return canApply ? t('MEASUREMENTS_MODULE.PUSH_APPLY') : t('MEASUREMENTS_MODULE.PUSH_ARMED_SELECT_TARGETS');
  };

  const handleClick = (): void => {
    if (canApply) {
      dispatch({ type: 'PUSH_MEASUREMENTS' });
      toasterMsg(t('MEASUREMENTS_MODULE.PUSHED', { count: targetCount }), 'success');
      return;
    }
    dispatch({ type: 'TOGGLE_PUSH_ARM' });
  };

  return (
    <button
      className={`btn p-0 bulk-measure-push-action${state.pushArmed ? ' is-armed' : ''}`}
      type="button"
      aria-label={actionLabel()}
      aria-pressed={state.pushArmed}
      disabled={!enabled}
      onClick={handleClick}
    >
      <PushIcon size={PUSH_ICON_SIZE} />
    </button>
  );
};

export default PushHeaderAction;
